import emailjs from '@emailjs/browser';
import { resolveClinicNameForPatientEmail } from '../utils/loginRestrictions';
import { buildPracticeSender, escapeHtml, practiceTemplateParams, resolvePracticeSender } from '../utils/practiceSender';

// ======== EMAILJS CONFIGURATION - FORCE CACHE BUST ========
// CRITICAL: Browser cache issue - forcing new timestamp
// Updated: 2025-06-11T19:35:00Z - TEMPLATE MUST BE template_766dhbd
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_868nett';
const EMAILJS_TEMPLATE_ID_REAL = 'template_766dhbd'; // HARDCODED TO FORCE UPDATE
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || EMAILJS_TEMPLATE_ID_REAL;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'tLLbISfeK4QbdmIJg';

function consentSubject(treatmentLabel, clinicName) {
  const base = `Action Required: ${treatmentLabel} Consent Form`;
  return clinicName ? `${base} - ${clinicName}` : base;
}

// Check if EmailJS is properly configured
const isEmailJSConfigured = () => {
  return EMAILJS_PUBLIC_KEY && 
         EMAILJS_PUBLIC_KEY !== 'your_public_key_here' &&
         EMAILJS_SERVICE_ID !== 'gmail' &&
         EMAILJS_TEMPLATE_ID !== 'consent_email';
};

// Initialize EmailJS (if you have the public key)
if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your_public_key_here') {
  emailjs.init(EMAILJS_PUBLIC_KEY);
  console.log('EmailJS initialized successfully');
  
  if (!isEmailJSConfigured()) {
    console.warn('⚠️ EmailJS is partially configured but using default service/template IDs. Please update your environment variables.');
  }
} else {
  console.warn('EmailJS public key not found. Email sending will be simulated.');
}

/**
 * Human-readable treatment line for patient emails (matches initial send / resend logic).
 */
export async function resolveConsentEmailTreatmentLabel(consent) {
  if (!consent) return 'Dental Treatment';
  if (consent.treatmentType === 'private' && consent.privateTreatmentDetails) {
    const s = String(consent.privateTreatmentDetails).trim();
    return s || 'Dental Treatment';
  }
  if (consent.treatmentType === 'mixed' && consent.mixedTreatmentDetails) {
    const s = String(consent.mixedTreatmentDetails).trim();
    return s || 'Dental Treatment';
  }
  if (consent.treatmentFor) {
    const s = String(consent.treatmentFor).trim();
    if (s) return s;
  }
  if (consent.treatmentId) {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const treatmentDoc = await getDoc(doc(db, 'treatments', consent.treatmentId));
      if (treatmentDoc.exists()) {
        const treatment = treatmentDoc.data();
        const t = (treatment.type || treatment.title || '').trim();
        if (t) return t;
      }
    } catch (e) {
      console.warn('[emailService] resolveConsentEmailTreatmentLabel:', e?.message || e);
    }
  }
  const raw = consent.treatmentType;
  if (raw && typeof raw === 'string') {
    const lower = raw.toLowerCase();
    if (!['private', 'nhs', 'mixed'].includes(lower)) {
      return raw.trim();
    }
  }
  return 'Dental Treatment';
}

export const emailService = {
  // Send consent form email to patient
  async sendConsentEmail(emailData) {
    const { 
      to, 
      patientName, 
      clinicId,
      clinicName: rawClinicName, 
      treatmentType, 
      consentLink, 
      expirationDate 
    } = emailData;

    const practice = await resolvePracticeSender({ clinicId, clinicName: rawClinicName });
    const clinicName = practice.name;
    const treatmentLabel = emailData.treatmentFor || treatmentType || 'Dental Treatment';

    // Template parameters for EmailJS with enhanced deliverability
    const templateParams = {
      to_email: to,
      to_name: patientName,
      clinic_name: clinicName,
      ...practiceTemplateParams(practice),
      treatment_type: treatmentType ? treatmentType.charAt(0).toUpperCase() + treatmentType.slice(1) : 'Dental Treatment',
      consent_link: consentLink,
      expiration_date: expirationDate,
      clinic_logo_url: emailData.clinic_logo_url || '', // Pass logo URL to EmailJS
      subject: consentSubject(treatmentLabel, clinicName),
      // Pass the complete HTML email content
      message_html: this.generateConsentEmailTemplate({
        patientName,
        clinicName,
        treatmentType: treatmentLabel,
        consentLink,
        expirationDate,
        practice
      }),
      // Also pass individual parameters for template compatibility
      patient_name: patientName,
      treatment_type: treatmentLabel,
      consent_link: consentLink,
      expiration_date: expirationDate
    };

    try {
      if (!isEmailJSConfigured()) {
        // Demo mode - log the email instead of sending
        console.log('=== EMAIL DEMO MODE ===');
        console.log('📧 REASON: EmailJS not configured properly');
        console.log('ℹ️  To enable real email sending:');
        console.log('   1. Visit https://dashboard.emailjs.com');
        console.log('   2. Create account and set up Gmail service');
        console.log('   3. Create email template');
        console.log('   4. Add environment variables to .env file');
        console.log('');
        console.log('To:', to);
        console.log('Subject:', templateParams.subject);
        console.log('Patient:', patientName);
        console.log('Treatment:', treatmentType);
        console.log('Link:', consentLink);
        console.log('Expires:', expirationDate);
        console.log('=====================');
        return { success: true, message: 'Email simulated (EmailJS not configured)' };
      }

      // Add detailed logging before sending
      console.log('📧 Sending email with EmailJS...');
      console.log('Service ID:', EMAILJS_SERVICE_ID);
      console.log('Template ID:', EMAILJS_TEMPLATE_ID);
      console.log('To email:', to);
      console.log('Template params:', templateParams);
      
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('✅ Email sent successfully to:', to);
      console.log('📨 EmailJS Response:', response);
      console.log('🔍 IMPORTANT - TELL YOUR PATIENT:');
      console.log('   📧 "Please check your spam/junk folder if you don\'t see the email"');
      console.log('   📱 "Add our email to your contacts to ensure delivery"');
      console.log('   ⏰ "The email may take a few minutes to arrive"');
      
      return { 
        success: true, 
        message: 'Email sent successfully - Please remind patient to check spam folder', 
        messageId: response.text,
        spamReminder: 'Important: Tell your patient to check their spam/junk folder if they don\'t see the email within 5 minutes.'
      };
    } catch (error) {
      console.error('Email sending error:', error);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to send email';
      if (error.text?.includes('service ID not found')) {
        errorMessage = `EmailJS service ID "${EMAILJS_SERVICE_ID}" not found. Please check your EmailJS dashboard and update REACT_APP_EMAILJS_SERVICE_ID in your .env file.`;
      } else if (error.text?.includes('template ID not found')) {
        errorMessage = `EmailJS template ID "${EMAILJS_TEMPLATE_ID}" not found. Please check your EmailJS dashboard and update REACT_APP_EMAILJS_TEMPLATE_ID in your .env file.`;
      } else if (error.text?.includes('public key')) {
        errorMessage = 'Invalid EmailJS public key. Please check REACT_APP_EMAILJS_PUBLIC_KEY in your .env file.';
      } else {
        errorMessage = `Failed to send email: ${error.message || error.text || 'Unknown error'}`;
      }
      
      throw new Error(errorMessage);
    }
  },

  // Generate HTML email template with improved deliverability
  generateConsentEmailTemplate({
    patientName,
    clinicName,
    treatmentType,
    consentLink,
    expirationDate,
    practice = buildPracticeSender(null, clinicName)
  }) {
    const clinic = resolveClinicNameForPatientEmail(clinicName);
    const clinicHeader = clinic
      ? `<p style="margin: 5px 0 0 0; opacity: 0.9;">${clinic}</p>`
      : '';
    const clinicLine = clinic ? `<br><strong>Clinic:</strong> ${clinic}` : '';
    const contactLine = clinic
      ? `Please contact ${clinic} directly for assistance.`
      : 'Please use the Chat to Clinic feature in the consent form, or contact your dental practice directly.';
    const thanksLine = clinic
      ? `Thank you for choosing ${clinic} for your healthcare needs.`
      : 'Thank you for reviewing this form.';

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta name="x-apple-disable-message-reformatting">
                     <title>Dental Consent Form Required - ${treatmentType}</title>
          <!--[if mso]>
          <noscript>
            <xml>
              <o:OfficeDocumentSettings>
                <o:PixelsPerInch>96</o:PixelsPerInch>
              </o:OfficeDocumentSettings>
            </xml>
          </noscript>
          <![endif]-->
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              line-height: 1.6; 
              color: #333333; 
              margin: 0; 
              padding: 0;
              background-color: #f8fafc;
            }
            .email-container { 
              max-width: 600px; 
              margin: 20px auto; 
              background-color: #ffffff;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            .header { 
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); 
              color: white; 
              padding: 30px 20px; 
              text-align: center; 
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: 700;
            }
            .content { 
              padding: 40px 30px; 
              background-color: #ffffff;
            }
            .greeting {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 20px;
              color: #1f2937;
            }
            .main-message {
              font-size: 16px;
              line-height: 1.7;
              margin-bottom: 25px;
              color: #374151;
            }
            .treatment-highlight {
              background-color: #eff6ff;
              border-left: 4px solid #2563eb;
              padding: 15px 20px;
              margin: 25px 0;
              border-radius: 0 8px 8px 0;
            }
            .cta-container {
              text-align: center;
              margin: 35px 0;
            }
            .cta-button { 
              display: inline-block; 
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); 
              color: white !important; 
              padding: 16px 32px; 
              text-decoration: none; 
              border-radius: 8px; 
              font-weight: 700;
              font-size: 16px;
              box-shadow: 0 4px 14px 0 rgba(37, 99, 235, 0.4);
              transition: all 0.2s ease;
            }
            .cta-button:hover {
              background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
              box-shadow: 0 6px 20px 0 rgba(37, 99, 235, 0.6);
            }
            .spam-warning { 
              background-color: #fef3c7; 
              border: 2px solid #f59e0b; 
              padding: 20px; 
              border-radius: 8px; 
              margin: 25px 0;
              text-align: center;
            }
            .spam-warning-title {
              font-weight: 700;
              color: #d97706;
              font-size: 16px;
              margin-bottom: 8px;
            }
            .spam-warning-text {
              color: #92400e;
              font-size: 14px;
              line-height: 1.5;
            }
            .warning { 
              background-color: #fee2e2; 
              border-left: 4px solid #ef4444; 
              padding: 20px; 
              border-radius: 0 8px 8px 0; 
              margin: 25px 0; 
            }
            .warning-title {
              font-weight: 700;
              color: #dc2626;
              margin-bottom: 8px;
            }
            .footer { 
              background-color: #f9fafb;
              padding: 30px; 
              border-top: 1px solid #e5e7eb; 
              font-size: 14px; 
              color: #6b7280; 
              text-align: center;
            }
            .contact-info {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin: 25px 0;
              text-align: center;
            }
            .backup-link {
              margin-top: 15px;
              padding: 15px;
              background-color: #f8fafc;
              border-radius: 6px;
              text-align: center;
            }
            .backup-link a {
              color: #2563eb;
              font-weight: 600;
              font-size: 15px;
              text-decoration: underline;
            }
            @media only screen and (max-width: 600px) {
              .email-container { margin: 10px; }
              .content { padding: 25px 20px; }
              .header { padding: 25px 15px; }
              .cta-button { padding: 14px 24px; font-size: 15px; }
            }
          </style>
        </head>
        <body>
          <div class="email-container">
                         <div class="header">
               <h1>Dental Consent Required</h1>
               ${clinicHeader}
             </div>
            
            <div class="content">
              <div class="greeting">Dear ${patientName},</div>
              
                             <div class="main-message">
                 We have prepared an important consent form that requires your review and digital signature for your upcoming dental treatment.
               </div>
              
              <div class="treatment-highlight">
                <strong>Treatment:</strong> ${treatmentType}${clinicLine}
              </div>
              
              <div class="spam-warning">
                <div class="spam-warning-title">📧 Important Email Delivery Notice</div>
                <div class="spam-warning-text">
                  This email may have been filtered to your <strong>spam or junk folder</strong>.<br>
                  Please check these folders if you cannot find this email.<br>
                  Add our email address to your contacts to ensure future delivery.
                </div>
              </div>
              
              <div class="main-message">
                Please review and sign your consent form by clicking the secure button below:
              </div>
              
              <div class="cta-container">
                <a href="${consentLink}" class="cta-button">
                  Review & Sign Consent Form
                </a>
              </div>
              
              <div class="warning">
                <div class="warning-title">⏰ Time Sensitive:</div>
                This secure link will expire on <strong>${expirationDate}</strong>. Please complete your consent form as soon as possible to avoid any delays with your appointment.
              </div>
              
              <div class="contact-info">
                <strong>Questions about your treatment or this form?</strong><br>
                ${contactLine}
              </div>
              
              <div class="main-message">
                ${thanksLine}
              </div>
              
              <div style="margin-top: 30px;">
                <span style="font-weight: 600;">Best regards,</span><br>
                ${practice.signatureHtml}
              </div>
            </div>
            
            <div class="footer">
              <p>${escapeHtml(practice.replyNote)}</p>
              
              <div class="backup-link">
                <strong>Button not working?</strong><br>
                <a href="${consentLink}">Open your consent form here</a>
              </div>
              
              <p style="margin-top: 20px; font-size: 12px; color: #9ca3af;">
                This email contains important medical information. Please keep it confidential.
              </p>
            </div>
          </div>
        </body>
      </html>
    `;
  },

  // Generate plain text email
  generateConsentEmailText({
    patientName,
    clinicName,
    treatmentType,
    consentLink,
    expirationDate
  }) {
    const clinic = resolveClinicNameForPatientEmail(clinicName);
    return `
Dear ${patientName},

 We have prepared a consent form for your upcoming ${treatmentType} dental treatment.

Please review and sign the consent form by visiting this link:
${consentLink}

IMPORTANT: This link will expire on ${expirationDate}. Please complete the consent form as soon as possible.

If you have any questions about the treatment or the consent form, please don't hesitate to contact our office.

Thank you,
${clinic || 'Smile Consent'}

---
This is an automated message from Smile Consent. Please do not reply to this email.
    `.trim();
  },

  // Send reminder email — EmailJS first (same path as initial consent sends), cloud function as backup
  async sendReminderEmail(emailData) {
    const { consentId, collectionName = 'aiConsents', to } = emailData;

    if (!consentId) {
      throw new Error('consentId is required to send reminder emails');
    }

    const sendViaCloud = async () => {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      const sendReminder = httpsCallable(functions, 'sendConsentReminderEmail');
      const result = await sendReminder({ consentId, collectionName });
      console.log('✅ Reminder email sent via cloud function:', result.data);
      return {
        success: true,
        message: 'Reminder email sent successfully',
        to: result.data?.to,
        provider: 'firebase',
      };
    };

    if (isEmailJSConfigured() && to) {
      try {
        const emailJsResult = await this._sendReminderEmailViaEmailJS(emailData);
        return { ...emailJsResult, provider: 'emailjs' };
      } catch (emailJsError) {
        console.warn('EmailJS reminder failed, trying cloud function:', emailJsError);
        return sendViaCloud();
      }
    }

    try {
      return await sendViaCloud();
    } catch (cloudError) {
      if (to && EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your_public_key_here') {
        console.warn('Cloud reminder failed, trying EmailJS fallback:', cloudError);
        return this._sendReminderEmailViaEmailJS(emailData);
      }
      throw cloudError;
    }
  },

  // EmailJS reminder delivery (primary for manual reminders in production)
  async _sendReminderEmailViaEmailJS(emailData) {
    const { 
      to, 
      patientName, 
      clinicId,
      clinicName, 
      treatmentType, 
      treatmentFor,
      consentLink, 
      expirationDate 
    } = emailData;

    const label = (treatmentFor || treatmentType || 'Dental Treatment').trim();
    const practice = await resolvePracticeSender({ clinicId, clinicName });
    const clinic = practice.name;
    const templateParams = {
      to_email: to,
      to_name: patientName,
      clinic_name: clinic,
      ...practiceTemplateParams(practice),
      treatment_type: label,
      treatment_for: label,
      consent_link: consentLink,
      expiration_date: expirationDate,
      subject: clinic
        ? `REMINDER: Action Required: ${label} Consent Form - ${clinic}`
        : `REMINDER: Action Required: ${label} Consent Form`,
      message_html: this.generateReminderEmailTemplate({
        patientName,
        clinicName: clinic,
        treatmentType: label,
        consentLink,
        expirationDate,
        practice
      })
    };

    try {
      if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your_public_key_here') {
        const response = await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams
        );
        console.log('✅ Reminder email sent successfully to:', to);
        console.log('📨 EmailJS Response:', response);
        console.log('🔍 IMPORTANT - TELL YOUR PATIENT:');
        console.log('   📧 "Please check your spam/junk folder for our reminder email"');
        console.log('   📱 "Add our email to your contacts to ensure delivery"');
        
        return { 
          success: true, 
          message: 'Reminder email sent successfully - Please remind patient to check spam folder', 
          messageId: response.text,
          spamReminder: 'Important: Tell your patient to check their spam/junk folder if they don\'t see the reminder email.'
        };
      } else {
        console.log('=== REMINDER EMAIL DEMO MODE ===');
        console.log('To:', to);
        console.log('Subject:', templateParams.subject);
        console.log('Patient:', patientName);
        console.log('Treatment:', label);
        console.log('Link:', consentLink);
        console.log('Expires:', expirationDate);
        console.log('===============================');
        return { success: true, message: 'Reminder email simulated (demo mode)' };
      }
    } catch (error) {
      console.error('Email reminder error:', error);
      throw new Error(`Failed to send reminder email: ${error.message || error.text || 'Unknown error'}`);
    }
  },

  // Reminder emails use the same blue layout as initial consent signing emails
  generateReminderEmailTemplate({
    patientName,
    clinicName,
    treatmentType,
    consentLink,
    expirationDate,
    practice = buildPracticeSender(null, clinicName)
  }) {
    const clinic = resolveClinicNameForPatientEmail(clinicName);
    const clinicHeader = clinic
      ? `<p style="margin: 5px 0 0 0; opacity: 0.9;">${clinic}</p>`
      : '';
    const clinicLine = clinic ? `<br><strong>Clinic:</strong> ${clinic}` : '';
    const thanksLine = clinic
      ? `Thank you for choosing ${clinic} for your healthcare needs.`
      : 'Thank you for reviewing this form.';

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta name="x-apple-disable-message-reformatting">
          <title>Consent Form Reminder - ${treatmentType}</title>
          <!--[if mso]>
          <noscript>
            <xml>
              <o:OfficeDocumentSettings>
                <o:PixelsPerInch>96</o:PixelsPerInch>
              </o:OfficeDocumentSettings>
            </xml>
          </noscript>
          <![endif]-->
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; 
              line-height: 1.6; 
              color: #333333; 
              margin: 0; 
              padding: 0;
              background-color: #f8fafc;
            }
            .email-container { 
              max-width: 600px; 
              margin: 20px auto; 
              background-color: #ffffff;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            .header { 
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); 
              color: white; 
              padding: 30px 20px; 
              text-align: center; 
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: 700;
            }
            .content { 
              padding: 40px 30px; 
              background-color: #ffffff;
            }
            .greeting {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 20px;
              color: #1f2937;
            }
            .main-message {
              font-size: 16px;
              line-height: 1.7;
              margin-bottom: 25px;
              color: #374151;
            }
            .treatment-highlight {
              background-color: #eff6ff;
              border-left: 4px solid #2563eb;
              padding: 15px 20px;
              margin: 25px 0;
              border-radius: 0 8px 8px 0;
            }
            .reminder-notice {
              background-color: #eff6ff;
              border: 2px solid #2563eb;
              padding: 20px;
              border-radius: 8px;
              margin: 25px 0;
              text-align: center;
              color: #1e40af;
              font-size: 16px;
              line-height: 1.6;
            }
            .cta-container {
              text-align: center;
              margin: 35px 0;
            }
            .cta-button { 
              display: inline-block; 
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); 
              color: white !important; 
              padding: 16px 32px; 
              text-decoration: none; 
              border-radius: 8px; 
              font-weight: 700;
              font-size: 16px;
              box-shadow: 0 4px 14px 0 rgba(37, 99, 235, 0.4);
            }
            .spam-warning { 
              background-color: #fef3c7; 
              border: 2px solid #f59e0b; 
              padding: 20px; 
              border-radius: 8px; 
              margin: 25px 0;
              text-align: center;
            }
            .spam-warning-title {
              font-weight: 700;
              color: #d97706;
              font-size: 16px;
              margin-bottom: 8px;
            }
            .spam-warning-text {
              color: #92400e;
              font-size: 14px;
              line-height: 1.5;
            }
            .warning { 
              background-color: #fee2e2; 
              border-left: 4px solid #ef4444; 
              padding: 20px; 
              border-radius: 0 8px 8px 0; 
              margin: 25px 0; 
            }
            .warning-title {
              font-weight: 700;
              color: #dc2626;
              margin-bottom: 8px;
            }
            .footer { 
              background-color: #f9fafb;
              padding: 30px; 
              border-top: 1px solid #e5e7eb; 
              font-size: 14px; 
              color: #6b7280; 
              text-align: center;
            }
            .contact-info {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin: 25px 0;
              text-align: center;
            }
            .backup-link {
              margin-top: 15px;
              padding: 15px;
              background-color: #f8fafc;
              border-radius: 6px;
              text-align: center;
            }
            .backup-link a {
              color: #2563eb;
              font-weight: 600;
              font-size: 15px;
              text-decoration: underline;
            }
          </style>
        </head>
        <body>
          <div class="email-container">
            <div class="header">
              <h1>Consent Form Reminder</h1>
              ${clinicHeader}
            </div>
            
            <div class="content">
              <div class="greeting">Dear ${patientName},</div>
              
              <div class="reminder-notice">
                <strong>This is a friendly reminder</strong> that you still have an unsigned consent form for your upcoming treatment.
              </div>
              
              <div class="treatment-highlight">
                <strong>Treatment:</strong> ${treatmentType}${clinicLine}
              </div>
              
              <div class="main-message">
                Please review and sign your consent form by clicking the secure button below:
              </div>
              
              <div class="cta-container">
                <a href="${consentLink}" class="cta-button">
                  Review & Sign Consent Form
                </a>
              </div>
              
              <div class="warning">
                <div class="warning-title">⏰ Time Sensitive:</div>
                This secure link will expire on <strong>${expirationDate}</strong>. Please complete your consent form as soon as possible to avoid any delays with your appointment.
              </div>
              
              <div class="main-message">
                ${thanksLine}
              </div>
              
              <div style="margin-top: 30px;">
                <span style="font-weight: 600;">Best regards,</span><br>
                ${practice.signatureHtml}
              </div>
            </div>
            
            <div class="footer">
              <p>${escapeHtml(practice.replyNote)}</p>
              
              <div class="backup-link">
                <strong>Button not working?</strong><br>
                <a href="${consentLink}">Open your consent form here</a>
              </div>
              
              <p style="margin-top: 20px; font-size: 12px; color: #9ca3af;">
                This email contains important medical information. Please keep it confidential.
              </p>
            </div>
          </div>
        </body>
      </html>
    `;
  }
};

// Make email service available globally for testing
if (typeof window !== 'undefined') {
  window.emailService = emailService;
  
  // Add diagnostic function for troubleshooting
  window.emailDiagnostic = () => {
    console.log('=== EMAIL SERVICE DIAGNOSTIC ===');
    console.log('🔧 EmailJS Configuration:');
    console.log('  Service ID:', EMAILJS_SERVICE_ID);
    console.log('  Template ID:', EMAILJS_TEMPLATE_ID);
    console.log('  Public Key:', EMAILJS_PUBLIC_KEY ? 'Set ✅' : 'Missing ❌');
    console.log('  Demo Mode:', !isEmailJSConfigured ? 'ON ⚠️' : 'OFF ✅');
    console.log('🔧 Browser Environment:');
    console.log('  EmailJS Library:', typeof emailjs !== 'undefined' ? 'Loaded ✅' : 'Missing ❌');
    console.log('  Window Object:', typeof window !== 'undefined' ? 'Available ✅' : 'Missing ❌');
    console.log('================================');
    
    if (!isEmailJSConfigured) {
      console.log('⚠️  EmailJS is not configured. Emails will be logged to console only.');
      console.log('📚 See ENVIRONMENT_SETUP.md for configuration instructions.');
    } else {
      console.log('✅ EmailJS appears to be configured correctly.');
      console.log('💡 If emails aren\'t delivering, check:');
      console.log('   1. Spam/Junk folder (most common)');
      console.log('   2. EmailJS Dashboard email history');
      console.log('   3. Email address accuracy in console logs');
    }
  };
} 